export type CandleBatch = { time: number; open: number; high: number; low: number; close: number }[];

export type HistoryState =
  | { kind: 'idle';      oldestTime: number; buffer: CandleBatch[] }
  | { kind: 'fetching';  oldestTime: number; buffer: CandleBatch[] }
  | { kind: 'exhausted'; oldestTime: number };

export type HistoryAction =
  | { type: 'RESET';          oldestTime: number }
  | { type: 'FETCH_START' }
  | { type: 'BATCH_APPENDED'; batch: CandleBatch; newOldestTime: number }
  | { type: 'EXHAUSTED' }
  | { type: 'FETCH_ERROR' }
  | { type: 'CONSUME' };

/** Pre-fetch ceiling — stop background fetches once the buffer reaches this depth. */
export const HIGH_WATER_BATCHES = 4;
/** Pipeline-fill target — auto-fetch until the buffer reaches this depth. */
export const TARGET_BUFFERED_BATCHES = 2;
/** Retry backoff after a transient fetch error — prevents a tight re-fetch loop. */
export const PIPELINE_BACKOFF_MS = 5000;

export function historyReducer(state: HistoryState, action: HistoryAction): HistoryState {
  switch (action.type) {
    case 'RESET':
      return { kind: 'idle', oldestTime: action.oldestTime, buffer: [] };
    case 'FETCH_START':
      if (state.kind !== 'idle') return state;
      return { ...state, kind: 'fetching' };
    case 'BATCH_APPENDED':
      if (state.kind !== 'fetching') return state;
      return { kind: 'idle', oldestTime: action.newOldestTime, buffer: [...state.buffer, action.batch] };
    case 'EXHAUSTED':
      if (state.kind !== 'fetching') return state;
      return { kind: 'exhausted', oldestTime: state.oldestTime };
    case 'FETCH_ERROR':
      // Network/timeout error — return to idle so the low-water trigger can retry.
      if (state.kind !== 'fetching') return state;
      return { kind: 'idle', oldestTime: state.oldestTime, buffer: state.buffer };
    case 'CONSUME':
      if (state.kind === 'exhausted' || state.buffer.length === 0) return state;
      return { ...state, buffer: state.buffer.slice(1) };
  }
}
